import { useState, useCallback } from 'react'
import { Head } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import AdminLayout from '@/components/admin/AdminLayout'
import PageHeader from '@/components/ui/PageHeader'
import Notification from '@/components/ui/Notification'
import DataTable, { useFlash, usePagination, type DataTableColumn } from '@/components/ui/DataTable'
import { FormSelect, FormInput } from '@/components/ui/form'
import Button from '@/components/ui/Button'
import Pagination from '@/components/ui/Pagination'
import { Badge } from '@/components/catalyst/badge'
import { Dialog, DialogTitle, DialogBody, DialogActions } from '@/components/catalyst/dialog'
import { TrashIcon, KeyIcon } from '@heroicons/react/24/outline'

// ─── Types ──────────────────────────────────────

interface PermissionRow {
  id: number
  scope: string
  action: string
  name: string
}

interface Props {
  permissions: PermissionRow[]
}

const PAGE_SIZES = [30, 50, 100, 200]

// ─── Component ──────────────────────────────────

export default function PermissionsIndex({ permissions: initialPermissions }: Props) {
  const [permissions, setPermissions] = useState(initialPermissions)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [formScope, setFormScope] = useState('')
  const [formAction, setFormAction] = useState('')
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [notifMessage, setNotifMessage] = useState('')
  const [showNotif, setShowNotif] = useState(false)
  const { flashRow } = useFlash()

  const csrfToken = typeof document !== 'undefined'
    ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    : ''
  const notify = useCallback((msg: string) => { setNotifMessage(msg); setShowNotif(true) }, [])

  const pageSizeOptions = PAGE_SIZES.map(n => ({ value: String(n), label: `${n} per page` }))
  const { pageSize, setPageSize, currentPage, setCurrentPage, totalPages, paginatedRows, totalCount } = usePagination(permissions)

  // ─── CRUD ───────────────────────────────────
  const openCreate = () => {
    setFormScope(''); setFormAction(''); setFormErrors([]); setDialogOpen(true)
  }

  const handleSubmit = async () => {
    if (!formScope.trim() || !formAction.trim()) { setFormErrors(['Scope and Action are required']); return }
    setFormErrors([]); setSubmitting(true)
    try {
      const res = await fetch('/admin/permissions.json', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ permission: { scope: formScope.trim(), action: formAction.trim() } }),
      })
      if (res.ok) {
        const data = await res.json()
        setPermissions(prev => [data, ...prev])
        setDialogOpen(false)
        notify('Saved:Permission created')
      } else {
        const err = await res.json().catch(() => ({}))
        setFormErrors(err.errors || ['Failed to save'])
      }
    } catch { setFormErrors(['Network error']) }
    setSubmitting(false)
  }

  const deletePermission = useCallback(async (id: number) => {
    if (!confirm('Are you sure you want to delete this permission?')) return
    try {
      const res = await fetch(`/admin/permissions/${id}.json`, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } })
      if (res.ok) { setPermissions(prev => prev.filter(p => p.id !== id)); notify('Deleted:Permission removed') }
    } catch { /* silent */ }
  }, [csrfToken, notify])

  // ─── Columns ────────────────────────────────
  const columns: DataTableColumn<PermissionRow>[] = [
    {
      key: 'scope', label: 'Scope',
      render: row => <Badge color="cyan">{row.scope}</Badge>,
      sortValue: row => row.scope,
      filterValue: row => row.scope,
    },
    {
      key: 'action', label: 'Action',
      render: row => <Badge color="amber">{row.action}</Badge>,
      sortValue: row => row.action,
      filterValue: row => row.action,
    },
    {
      key: 'name', label: 'Name',
      render: row => <span className="font-medium text-gray-900 dark:text-white font-mono text-sm">{row.name}</span>,
      sortValue: row => row.name,
      filterValue: row => row.name,
    },
  ]

  return (
    <>
      <Head title="Admin - Permissions" />
      <Notification message={notifMessage} show={showNotif} onClose={() => setShowNotif(false)} />
      <AdminLayout>
      <main>
      <PageHeader title="Permissions" icon={<KeyIcon className="h-5 w-5" />} actions={
        <div className="flex items-center gap-2">
          <div className="w-36"><FormSelect value={String(pageSize)} onChange={v => { setPageSize(Number(v)); setCurrentPage(0) }} options={pageSizeOptions} /></div>
          <Button onClick={openCreate}>+ New Permission</Button>
        </div>
      } />

      <div className="mb-2"><Badge color="zinc"><span className="text-xs font-medium">{totalCount} permissions</span></Badge></div>

      <DataTable
        rows={paginatedRows} columns={columns} rowKey={row => row.id}
        flashRowId={flashRow?.id ?? null} flashStatus={flashRow?.status ?? null}
        emptyText="No permissions" filteredEmptyText="No permissions match your filters"
        actions={row => (
          <div className="flex items-center justify-end gap-1">
            <button onClick={() => deletePermission(row.id)} className="rounded p-1 text-zinc-400 hover:text-red-600 dark:text-zinc-500 dark:hover:text-red-400" title="Delete"><TrashIcon className="h-4 w-4" /></button>
          </div>
        )}
      />

      <Pagination currentPage={currentPage} totalPages={totalPages} pageSize={pageSize} totalCount={totalCount} onPageChange={setCurrentPage} />

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>New Permission</DialogTitle>
        <DialogBody>
          {formErrors.length > 0 && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2 dark:border-red-800 dark:bg-red-900/20">
              <ul className="list-disc pl-4 text-xs text-red-700 dark:text-red-400">{formErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Scope *</label>
              <FormInput value={formScope} onChange={e => setFormScope((e.target as HTMLInputElement).value)} placeholder="e.g. Menu, Order..." autoFocus />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Action *</label>
              <FormInput value={formAction} onChange={e => setFormAction((e.target as HTMLInputElement).value)} placeholder="e.g. view, edit, delete..." />
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button disabled={submitting} onClick={handleSubmit}>{submitting ? 'Creating...' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
      </main>
      </AdminLayout>
    </>
  )
}

PermissionsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
